/**
 * State of the report and artifact canvas, kept free of React and network calls.
 *
 * A document canvas is an ordered list of full-width sections. Data sections
 * (headline number, chart, table) are planned one by one into governed charts
 * in the browser, where any clarification happens. Written sections (overview,
 * key insights, summary) only carry an instruction: they are written on the
 * server from the data sections' rows when the document is composed.
 */
import type {
  DocumentSectionDraft,
  DocumentSectionKind,
  PanelDraft,
  PanelPlan,
} from "../../services/api";
import {
  clarifiedQuestion,
  MAX_QUESTION_CHARS,
  type FailureDetail,
  type PlanStop,
} from "../canvas/canvasModel";

export const MAX_SECTIONS = 10;
export const MAX_INSTRUCTION_CHARS = MAX_QUESTION_CHARS;
export const MAX_TITLE_CHARS = 160;

/** A sensible document to start from; every row can be changed or removed. */
export const DEFAULT_KINDS: readonly DocumentSectionKind[] = [
  "overview",
  "headline_number",
  "table",
  "key_insights",
  "summary",
];

export const SECTION_KINDS: readonly DocumentSectionKind[] = [
  "overview",
  "headline_number",
  "chart",
  "table",
  "key_insights",
  "summary",
];

const DATA_KINDS: ReadonlySet<DocumentSectionKind> = new Set([
  "headline_number",
  "chart",
  "table",
]);

/**
 * Appended to a data section's question before planning, so the planner picks
 * measures that suit how the section will be shown. The server enforces the
 * shape regardless; this only improves what it is given.
 */
const PLAN_HINTS: Partial<Record<DocumentSectionKind, string>> = {
  headline_number: "(as a single total figure)",
  table: "(as a table)",
};

export type RowPhase =
  /** Editable. Written rows stay here until the document is composed. */
  | "draft"
  /** Waiting for the agent to turn the question into a chart. */
  | "planning"
  /** A chart is chosen; it runs when the document is composed. */
  | "planned"
  | PlanStop;

export interface DocumentRow {
  /** Stable id; never changes while the canvas is open. */
  id: string;
  kind: DocumentSectionKind;
  instruction: string;
  phase: RowPhase;
  message: string;
  options: string[];
  candidates: PanelDraft[];
  panel: PanelDraft | null;
  retryable: boolean;
}

export function isDataKind(kind: DocumentSectionKind): boolean {
  return DATA_KINDS.has(kind);
}

export function createRow(kind: DocumentSectionKind, id: string): DocumentRow {
  return {
    id,
    kind,
    instruction: "",
    phase: "draft",
    message: "",
    options: [],
    candidates: [],
    panel: null,
    retryable: false,
  };
}

export function createRows(
  newId: () => string,
  kinds: readonly DocumentSectionKind[] = DEFAULT_KINDS,
): DocumentRow[] {
  return kinds.map((kind) => createRow(kind, newId()));
}

/** Why an instruction cannot be used yet, or null when it can. */
export function instructionProblem(instruction: string): string | null {
  const text = instruction.trim();
  if (!text) return "Type what this section should contain.";
  if (text.length > MAX_INSTRUCTION_CHARS) {
    return `Keep it under ${MAX_INSTRUCTION_CHARS} characters.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// List operations. Each returns the same array when nothing changes, so a
// React state update is skipped.

export function addRow(rows: DocumentRow[], kind: DocumentSectionKind, id: string): DocumentRow[] {
  if (rows.length >= MAX_SECTIONS) return rows;
  return [...rows, createRow(kind, id)];
}

/** The last row cannot be removed: a document needs at least one section. */
export function removeRow(rows: DocumentRow[], id: string): DocumentRow[] {
  if (rows.length <= 1) return rows;
  const next = rows.filter((row) => row.id !== id);
  return next.length === rows.length ? rows : next;
}

export function moveRow(rows: DocumentRow[], id: string, offset: -1 | 1): DocumentRow[] {
  const from = rows.findIndex((row) => row.id === id);
  const to = from + offset;
  if (from < 0 || to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  const [row] = next.splice(from, 1);
  if (!row) return rows;
  next.splice(to, 0, row);
  return next;
}

export function updateRow(
  rows: DocumentRow[],
  id: string,
  change: (row: DocumentRow) => DocumentRow,
): DocumentRow[] {
  return rows.map((row) => (row.id === id ? change(row) : row));
}

// ---------------------------------------------------------------------------
// Row transitions.

function reset(row: DocumentRow): DocumentRow {
  return { ...row, message: "", options: [], candidates: [], retryable: false };
}

/** Back to an editable instruction. Any planned chart is forgotten. */
export function editing(row: DocumentRow): DocumentRow {
  return { ...reset(row), phase: "draft", panel: null };
}

export function withInstruction(row: DocumentRow, instruction: string): DocumentRow {
  return { ...row, instruction };
}

/** A different kind needs a different plan, so the row goes back to draft. */
export function withKind(row: DocumentRow, kind: DocumentSectionKind): DocumentRow {
  if (row.kind === kind) return row;
  return { ...editing(row), kind };
}

export function planning(row: DocumentRow, instruction = row.instruction): DocumentRow {
  return { ...reset(row), instruction, phase: "planning", panel: null };
}

export function planned(row: DocumentRow, panel: PanelDraft): DocumentRow {
  return { ...reset(row), phase: "planned", panel };
}

export function failed(row: DocumentRow, detail: FailureDetail): DocumentRow {
  return {
    ...reset(row),
    phase: "failed",
    panel: null,
    message: detail.message,
    retryable: detail.retryable,
  };
}

/** The question sent to the planner for a data row. */
export function planQuestion(row: DocumentRow, instruction = row.instruction): string {
  const text = instruction.trim();
  const hint = PLAN_HINTS[row.kind];
  return (hint ? `${text} ${hint}` : text).slice(0, MAX_INSTRUCTION_CHARS);
}

export function clarifiedInstruction(instruction: string, option: string): string {
  return clarifiedQuestion(instruction, option).slice(0, MAX_INSTRUCTION_CHARS);
}

export function applyPlan(row: DocumentRow, plan: PanelPlan): DocumentRow {
  const base = { ...reset(row), panel: null };
  switch (plan.status) {
    case "ok": {
      const [panel] = plan.panels;
      if (!panel) {
        return failed(base, { message: "The agent returned no chart. Try again.", retryable: true });
      }
      return planned(base, panel);
    }
    case "multiple":
      return {
        ...base,
        phase: "multiple",
        message: plan.message || "This question could be answered several ways. Keep one.",
        candidates: plan.panels,
      };
    case "clarify":
      return {
        ...base,
        phase: "clarify",
        message: plan.message || "Which did you mean?",
        options: plan.options,
      };
    case "unsupported":
    case "invalid":
      return {
        ...base,
        phase: "unsupported",
        message: plan.message || "This cannot be answered from the semantic model. Try rephrasing it.",
      };
  }
}

export function keepCandidate(row: DocumentRow, index: number): DocumentRow {
  const candidate = row.candidates[index];
  return candidate ? planned(row, candidate) : row;
}

// ---------------------------------------------------------------------------
// Whole-document checks.

export function dataRows(rows: DocumentRow[]): DocumentRow[] {
  return rows.filter((row) => isDataKind(row.kind));
}

export function isPlanning(rows: DocumentRow[]): boolean {
  return rows.some((row) => row.phase === "planning");
}

/** Data rows that still need the user: not yet planned and not in flight. */
export function waitingRows(rows: DocumentRow[]): DocumentRow[] {
  return dataRows(rows).filter((row) => row.phase !== "planned" && row.phase !== "planning");
}

/**
 * Why the canvas cannot be started, or null when it can. Checked before any
 * row is planned, so the user fixes everything in one pass.
 */
export function documentProblem(rows: DocumentRow[]): string | null {
  if (rows.length === 0) return "Add at least one section.";
  if (dataRows(rows).length === 0) {
    return "Add a headline number, chart or table. Written sections are composed from their figures.";
  }
  if (rows.some((row) => instructionProblem(row.instruction) !== null)) {
    return "Fill in every section, or remove the ones you do not need.";
  }
  return null;
}

/** Every data row has its chart and every written row has its instruction. */
export function readyToCompose(rows: DocumentRow[]): boolean {
  return (
    documentProblem(rows) === null &&
    dataRows(rows).every((row) => row.phase === "planned" && row.panel !== null)
  );
}

/** The request sections, in canvas order. Call only when `readyToCompose`. */
export function requestSections(rows: DocumentRow[]): DocumentSectionDraft[] {
  return rows.map((row) => {
    const instruction = row.instruction.trim();
    if (isDataKind(row.kind) && row.panel) {
      return { kind: row.kind, instruction, panel: row.panel };
    }
    return { kind: row.kind, instruction };
  });
}
